/**
 * Queue Infrastructure - API Client
 * Uses getHttpClient() from @spcc/core for all requests (proper auth + base URL).
 */

import { getHttpClient } from '@spcc/core'
import { API_ENDPOINTS } from '../domain/constants'
import type {
  LoadingLocationFilter,
  LoadingLocationFormData,
  LoadingLocationRuleFilter,
  LoadingLocationRuleFormData,
  QueueEntryFilter,
  JoinQueueData,
  ProductWaitTime,
} from '../domain/types'
import {
  buildLocationQueryString,
  buildEntryQueryString,
  buildRuleQueryString,
  transformToCreateLocationRequest,
  transformToLocationRequest,
  transformToJoinQueueRequest,
  transformToLoadingLocationRuleRequest,
  waitTimesResponseSchema,
} from '../api'

// ============================================================================
// Loading Location API
// ============================================================================

export const loadingLocationsApi = {
  async getAll(filter?: LoadingLocationFilter) {
    const http = getHttpClient()
    const query = filter ? `?${buildLocationQueryString(filter)}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.LOADING_LOCATIONS}${query}`)
    return res.data
  },

  async getById(id: string) {
    const http = getHttpClient()
    const res = await http.get<any>(`${API_ENDPOINTS.LOADING_LOCATION_BY_ID(id)}?populate=*`)
    return res.data
  },

  async getQueue(id: string) {
    const http = getHttpClient()
    const res = await http.get<any>(API_ENDPOINTS.LOADING_LOCATION_QUEUE(id))
    return res.data
  },

  async getStats(id: string, startDate?: string, endDate?: string) {
    const http = getHttpClient()
    const params = new URLSearchParams()
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    const query = params.toString() ? `?${params.toString()}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.LOADING_LOCATION_STATS(id)}${query}`)
    return res.data
  },

  async getDashboard(tenantId?: string, factoryId?: string) {
    const http = getHttpClient()
    const params = new URLSearchParams()
    if (tenantId) params.append('tenantId', tenantId)
    if (factoryId) params.append('factoryId', factoryId)
    const query = params.toString() ? `?${params.toString()}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.LOADING_LOCATIONS_DASHBOARD}${query}`)
    return res.data
  },

  async getByVariant(variantId: string, tenantId?: string, factoryId?: string) {
    const http = getHttpClient()
    const params = new URLSearchParams()
    if (tenantId) params.append('tenantId', tenantId)
    if (factoryId) params.append('factoryId', factoryId)
    const query = params.toString() ? `?${params.toString()}` : ''
    const res = await http.get<any>(
      `${API_ENDPOINTS.LOADING_LOCATIONS_BY_VARIANT(variantId)}${query}`
    )
    return res.data
  },

  async create(data: LoadingLocationFormData) {
    const http = getHttpClient()
    const res = await http.post<any>(`${API_ENDPOINTS.LOADING_LOCATIONS}?populate=*`, {
      data: transformToCreateLocationRequest(data),
    })
    return res.data
  },

  async update(id: string, data: Partial<LoadingLocationFormData>) {
    const http = getHttpClient()
    const res = await http.put<any>(`${API_ENDPOINTS.LOADING_LOCATION_BY_ID(id)}?populate=*`, {
      data: transformToLocationRequest(data),
    })
    return res.data
  },

  async delete(id: string) {
    const http = getHttpClient()
    const res = await http.delete<any>(API_ENDPOINTS.LOADING_LOCATION_BY_ID(id))
    return res.data
  },

  async getTimeline(documentId: string) {
    const http = getHttpClient()
    const res = await http.get<any>(API_ENDPOINTS.LOADING_LOCATION_TIMELINE(documentId))
    return (res.data?.data ?? []) as import('../domain/types').LoadingLocationEvent[]
  },
}

// ============================================================================
// Queue Entry API
// ============================================================================

export const queueEntriesApi = {
  async getAll(filter?: QueueEntryFilter) {
    const http = getHttpClient()
    const query = filter ? `?${buildEntryQueryString(filter)}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.QUEUE_ENTRIES}${query}`)
    return res.data
  },

  async getById(id: string) {
    const http = getHttpClient()
    const res = await http.get<any>(`${API_ENDPOINTS.QUEUE_ENTRY_BY_ID(id)}?populate=*`)
    return res.data
  },

  async getMyEntries(status?: string) {
    const http = getHttpClient()
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    const query = params.toString() ? `?${params.toString()}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.QUEUE_MY_ENTRIES}${query}`)
    return res.data
  },

  async join(data: JoinQueueData) {
    const http = getHttpClient()
    const res = await http.post<any>(API_ENDPOINTS.QUEUE_JOIN, transformToJoinQueueRequest(data))
    return res.data
  },

  async cancel(id: string, reason?: string, cancelledBy?: string) {
    const http = getHttpClient()
    const res = await http.post<any>(API_ENDPOINTS.QUEUE_CANCEL(id), { reason, cancelledBy })
    return res.data
  },

  async refreshQueue(locationId: string) {
    const http = getHttpClient()
    const res = await http.post<any>(API_ENDPOINTS.QUEUE_REFRESH(locationId), {})
    return res.data
  },
}

// ============================================================================
// Queue Events API
// ============================================================================

export const queueEventsApi = {
  async getAll(limit?: number) {
    const http = getHttpClient()
    const params = new URLSearchParams()
    if (limit) params.append('limit', String(limit))
    const query = params.toString() ? `?${params.toString()}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.QUEUE_EVENTS}${query}`)
    return res.data
  },

  async getByEntry(entryId: string) {
    const http = getHttpClient()
    const res = await http.get<any>(API_ENDPOINTS.QUEUE_EVENTS_BY_ENTRY(entryId))
    return res.data
  },

  async getRecent(limit?: number, tenantId?: string, eventType?: string, loadingLocationId?: string) {
    const http = getHttpClient()
    const params = new URLSearchParams()
    if (limit) params.append('limit', String(limit))
    if (tenantId) params.append('tenantId', tenantId)
    if (eventType) params.append('eventType', eventType)
    if (loadingLocationId) params.append('loadingLocationId', loadingLocationId)
    const query = params.toString() ? `?${params.toString()}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.QUEUE_EVENTS_RECENT}${query}`)
    return res.data
  },
}

// ============================================================================
// Loading Location Rules API
// ============================================================================

export const loadingLocationRulesApi = {
  async getAll(filter?: LoadingLocationRuleFilter) {
    const http = getHttpClient()
    const query = filter ? `?${buildRuleQueryString(filter)}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.LOADING_LOCATION_RULES}${query}`)
    return res.data
  },

  async getById(id: string) {
    const http = getHttpClient()
    const res = await http.get<any>(`${API_ENDPOINTS.LOADING_LOCATION_RULE_BY_ID(id)}?populate=*`)
    return res.data
  },

  async create(data: LoadingLocationRuleFormData) {
    const http = getHttpClient()
    const res = await http.post<any>(`${API_ENDPOINTS.LOADING_LOCATION_RULES}?populate=*`, {
      data: transformToLoadingLocationRuleRequest(data),
    })
    return res.data
  },

  async update(id: string, data: Partial<LoadingLocationRuleFormData>) {
    const http = getHttpClient()
    const res = await http.put<any>(`${API_ENDPOINTS.LOADING_LOCATION_RULE_BY_ID(id)}?populate=*`, {
      data: transformToLoadingLocationRuleRequest(data as LoadingLocationRuleFormData),
    })
    return res.data
  },

  async delete(id: string) {
    const http = getHttpClient()
    const res = await http.delete<any>(API_ENDPOINTS.LOADING_LOCATION_RULE_BY_ID(id))
    return res.data
  },
}

// Combined export
export const waitTimesApi = {
  /** Live wait per cement type at a plant, names in `locale`. */
  async getForFactory(factoryId: string, locale: string): Promise<ProductWaitTime[]> {
    const response = await getHttpClient().get<unknown>(
      `${API_ENDPOINTS.WAIT_TIMES}?locale=${encodeURIComponent(locale)}`,
      { headers: { 'x-factory-id': factoryId } }
    )
    // Pictures and categories are the portal's to add, from the product catalog.
    return waitTimesResponseSchema.parse(response.data).data.map((w) => ({
      ...w,
      imageUrl: null,
      imageLargeUrl: null,
      category: null,
    }))
  },
}

export const queueApi = {
  loadingLocations: loadingLocationsApi,
  waitTimes: waitTimesApi,
  queueEntries: queueEntriesApi,
  queueEvents: queueEventsApi,
  loadingLocationRules: loadingLocationRulesApi,
}

// ============================================================================
// Reference Data API  (variants + factories for form dropdowns)
// ============================================================================

export const referenceDataApi = {
  async searchLoadingLocations(query: string): Promise<any[]> {
    const http = getHttpClient()
    const res = await http.get<{ data: any[] }>(
      `/api/loading-locations?fields[0]=documentId&fields[1]=label&filters[label][$containsi]=${encodeURIComponent(query)}&pagination[pageSize]=50`
    )
    return res.data?.data ?? []
  },

  async getVariants(): Promise<any[]> {
    const http = getHttpClient()
    const res = await http.get<{ data: any[] }>(
      '/api/variants?fields[0]=documentId&fields[1]=name&fields[2]=variantCode&fields[3]=packaging&filters[isActive][$eq]=true&sort=variantCode:asc&pagination[pageSize]=200'
    )
    return res.data?.data ?? []
  },

  async getCities(): Promise<any[]> {
    const http = getHttpClient()
    const res = await http.get<{ data: any[] }>(
      '/api/cities?locale=&fields[0]=documentId&fields[1]=name&fields[2]=locale&populate[localizations][fields][0]=name&populate[localizations][fields][1]=locale&sort=name:asc&pagination[pageSize]=500'
    )
    return res.data?.data ?? []
  },

  async searchCities(query: string): Promise<any[]> {
    const http = getHttpClient()
    const q = encodeURIComponent(query)
    const res = await http.get<{ data: any[] }>(
      `/api/cities?locale=&fields[0]=documentId&fields[1]=name&fields[2]=salesDistrict&fields[3]=locale&populate[localizations][fields][0]=name&populate[localizations][fields][1]=locale&filters[$or][0][name][$containsi]=${q}&filters[$or][1][salesDistrict][$containsi]=${q}&sort=name:asc&pagination[pageSize]=50`
    )
    return res.data?.data ?? []
  },

  async getTenants(): Promise<any[]> {
    const http = getHttpClient()
    const res = await http.get<{ data: any[] }>(
      '/api/tenants?fields[0]=documentId&fields[1]=name&sort=name:asc&pagination[pageSize]=200'
    )
    return res.data?.data ?? []
  },

  async searchTenants(query: string): Promise<any[]> {
    const http = getHttpClient()
    const q = encodeURIComponent(query)
    const res = await http.get<{ data: any[] }>(
      `/api/tenants?fields[0]=documentId&fields[1]=name&fields[2]=code&filters[$or][0][name][$containsi]=${q}&filters[$or][1][code][$containsi]=${q}&sort=name:asc&pagination[pageSize]=50`
    )
    return res.data?.data ?? []
  },

  async getFactories(): Promise<any[]> {
    const http = getHttpClient()
    const res = await http.get<{ data: any[] }>(
      '/api/factories?fields[0]=documentId&fields[1]=name&filters[factoryStatus][$eq]=active&sort=name:asc&pagination[pageSize]=200'
    )
    return res.data?.data ?? []
  },
}

/**
 * Move a waiting truck within its queue.
 *
 * Who did it is NOT sent: the server takes the actor from the authenticated
 * user. A caller-supplied name is worth nothing in an audit trail, and this
 * record exists precisely so that jumping the line is visible afterwards.
 */
export const queueReorderApi = {
  async reorder(entryId: string | number, target: unknown, reason: string) {
    const http = getHttpClient()
    const res = await http.post<any>(API_ENDPOINTS.QUEUE_REORDER(String(entryId)), {
      target,
      reason,
    })
    return res.data?.data ?? res.data
  },
}

// ============================================================================
// Queue Simulation API
//
// A read-only sandbox. Every call here reads the yard or runs arithmetic on the
// server; none of them changes a queue, a bay, or a truck.
//
// The seed is sent back with every run because the server keeps no session:
// it replays the command list from the seed each time, and the engine is
// deterministic, so the same commands always land in the same yard.
// ============================================================================

export const queueSimulationApi = {
  /**
   * The starting yard.
   *
   * `empty` keeps the real bays, products and settings but drops the trucks,
   * for composing a day rather than inspecting the one in progress.
   */
  async getSeed(factoryId?: string | number, empty?: boolean) {
    const http = getHttpClient()
    const query = new URLSearchParams()
    if (factoryId) query.append('factoryId', String(factoryId))
    if (empty) query.append('empty', 'true')
    const qs = query.toString() ? `?${query.toString()}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.QUEUE_SIMULATION_SEED}${qs}`)
    return res.data?.data ?? res.data
  },

  /** Past days with enough queue history to be worth replaying. */
  async getReplayableDays(factoryId?: string | number) {
    const http = getHttpClient()
    const query = factoryId ? `?factoryId=${encodeURIComponent(String(factoryId))}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.QUEUE_SIMULATION_DAYS}${query}`)
    return res.data?.data ?? res.data
  },

  /** One past day, rebuilt as a runnable simulation. */
  async getReplaySeed(date: string, factoryId?: string | number) {
    const http = getHttpClient()
    const query = new URLSearchParams({ date })
    if (factoryId) query.append('factoryId', String(factoryId))
    const res = await http.get<any>(`${API_ENDPOINTS.QUEUE_SIMULATION_REPLAY}?${query.toString()}`)
    return res.data?.data ?? res.data
  },

  /** Real orders not currently in the queue, to send into a simulation. */
  async getAvailableOrders(params?: { factoryId?: string | number; search?: string; limit?: number }) {
    const http = getHttpClient()
    const query = new URLSearchParams()
    if (params?.factoryId) query.append('factoryId', String(params.factoryId))
    if (params?.search) query.append('search', params.search)
    if (params?.limit) query.append('limit', String(params.limit))
    const qs = query.toString() ? `?${query.toString()}` : ''
    const res = await http.get<any>(`${API_ENDPOINTS.QUEUE_SIMULATION_ORDERS}${qs}`)
    return res.data?.data ?? res.data
  },

  /** Run the seeded yard forward, optionally under several sets of rules. */
  async run(request: unknown) {
    const http = getHttpClient()
    const res = await http.post<any>(API_ENDPOINTS.QUEUE_SIMULATION_RUN, request)
    return res.data?.data ?? res.data
  },
}
